import React, { useEffect, useState } from "react";
import { Box, Typography, Switch } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BeneficiaryModal from "../../../../../../modals/BeneficiaryModal";
import ManageConfigurationModal from "../../../../../../modals/ManageConfigurationModal/index.jsx";
import style from "./style";
import { useAlert } from "../../../../../../context/AlertContext/index.js";

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatCombinedValue = (value) => {
  if (value === null || value === undefined) return "—";
  return Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, "");
};

const sumNumbers = (values = []) => {
  let total = 0;
  let hasValue = false;
  values.forEach((val) => {
    const numeric = typeof val === "number" ? val : toNumber(val);
    if (numeric !== null && !Number.isNaN(numeric)) {
      total += numeric;
      hasValue = true;
    }
  });
  return hasValue ? total : null;
};

const isBothApplicable = (value) =>
  typeof value === "string" && value.toLowerCase() === "both";

const buildTotalDisplay = ({
  applicableOn,
  employeeValues = [],
  employerValues = [],
  fallbacks = [],
}) => {
  const employeeNumber = employeeValues.reduce(
    (acc, current) => (acc !== null ? acc : toNumber(current)),
    null
  );
  const employerNumber = employerValues.reduce(
    (acc, current) => (acc !== null ? acc : toNumber(current)),
    null
  );

  if (isBothApplicable(applicableOn)) {
    const combined = sumNumbers([employeeNumber, employerNumber]);
    return formatCombinedValue(combined);
  }

  const fallbackNumber = fallbacks.reduce(
    (acc, current) => (acc !== null ? acc : toNumber(current)),
    null
  );

  // Check applicableOn to determine which value to display
  const applicableOnLower = typeof applicableOn === 'string' ? applicableOn.toLowerCase() : '';
  
  if (applicableOnLower === 'employer') {
    const finalValue = employerNumber ?? fallbackNumber;
    return formatCombinedValue(finalValue);
  }
  
  if (applicableOnLower === 'employee') {
    const finalValue = employeeNumber ?? fallbackNumber;
    return formatCombinedValue(finalValue);
  }

  // Fallback to original logic if applicableOn doesn't match expected values
  const finalValue = employeeNumber ?? employerNumber ?? fallbackNumber;
  return formatCombinedValue(finalValue);
};


const additionalInformation = ({
  handleFringeBenefit,
  handleAllowance,
  getMedical,
  handleLevies,
  allowanceType,
  fringeType,
  leviesType,
  getAllowance,
  getFringeBenefit,
  getLevies,
  getPension,
  getContributionsdata,
  handleContributions,
  contributionType,
  creditsListData,
  handleCreditApply,
  handlePostCreditApply,
  userData,
  handleBeniSaved,
  handleUpdateBeneficiary,
  beneficiaries,
  handleDeleteBeneficiary,
  getAllBenificiaries,
  handleAllContribution,
  handleContributionDelete,
  handleAllowancesDelete,
  handleFringeBenefitsDelete,
  handleLeviesDelete,
  getAllCredits,
  singleCreditTypes,
  employeeBeneficiaryValues,
  refreshSingleCreditTypes,
  taxRulesListData,
  handleTaxRuleApply,
  refreshTaxRules,
  getLeaves,
  handleLeaveBalanceCreate,
  handleLeaveBalanceUpdate,
  refreshLeaves,
  handleLeaveBalanceDelete
}) => {
  const [isBeneficiaryModalOpen, setIsBeneficiaryModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [creditId, setCreditId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [apiError, setApiError] = useState('');
  const [creditToggleState, setCreditToggleState] = useState({});
  const [taxRulesToggleState, setTaxRulesToggleState] = useState({});
  const [leavesToggleState, setLeavesToggleState] = useState({});
  const { showAlert } = useAlert();

  useEffect(() => {
    if (!Array.isArray(creditsListData)) return;

    setCreditToggleState((prev) => {
      const nextState = {};
      creditsListData.forEach((item, index) => {
        const key = item?._id || index;
        const apiValue = item?.isApplicable === true;
        
        if (prev[key] !== undefined) {
          nextState[key] = prev[key];
        } else {
          nextState[key] = apiValue;
        }
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextState);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === nextState[key])
      ) {
        return prev;
      }

      return nextState;
    });
  }, [creditsListData]);

  useEffect(() => {
    if (!Array.isArray(taxRulesListData)) return;

    setTaxRulesToggleState((prev) => {
      const nextState = {};
      taxRulesListData.forEach((item, index) => {
        const key = item?._id || item?.taxRule || item?.taxRuleId || index;
        nextState[key] = item?.isAssigned || item?.isApplicable || false;
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextState);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === nextState[key])
      ) {
        return prev;
      }

      return nextState;
    });
  }, [taxRulesListData]);

  useEffect(() => {
    if (!Array.isArray(getLeaves)) return;

    setLeavesToggleState((prev) => {
      const nextState = {};
      getLeaves.forEach((item, index) => {
        const key = item?._id || index;
        const apiValue = item?.isApplicable === true;
        
        if (prev[key] !== undefined) {
          nextState[key] = prev[key];
        } else {
          nextState[key] = apiValue;
        }
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextState);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === nextState[key])
      ) {
        return prev;
      }

      return nextState;
    });
  }, [getLeaves]);

  const closeModal = () => {
    setActiveModal(null);
    setSelectedItem(null);
    setIsEditMode(false);
  };

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>
            Additional Information
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: "15px", mt: 3, mb: 3 }}>
        <Box sx={style.creditsHeader}>
          <Typography sx={style.creditsTitle}>Tax Contribution</Typography>
        </Box>
        <Box sx={style.creditsTableContainer}>
          <Box sx={style.creditsTable}>
            <Box sx={style.creditsTableHeader}>
              <Box sx={style.creditsThCreditType}>Tax Rule Type</Box>
              <Box sx={style.creditsThMode}>Mode</Box>
              <Box sx={style.creditsThApplicableOn}>Applicable on</Box>
              <Box sx={style.creditsThTotalCredits}>Employee Rate</Box>
              <Box sx={style.creditsThTotalCredits}>Employer Rate</Box>
              <Box sx={style.creditsThActions}>Actions</Box>
            </Box>
            {Array.isArray(taxRulesListData) && taxRulesListData.length > 0 ? (
              taxRulesListData.map((item, idx) => {
                const toggleKey = item?._id || item?.taxRule || item?.taxRuleId || idx;
                const persistedToggleValue = taxRulesToggleState[toggleKey];
                const currentToggleValue =
                  persistedToggleValue !== undefined
                    ? persistedToggleValue
                    : item?.isAssigned || item?.isApplicable || false;

                return (
                  <Box key={idx} sx={style.creditsTableRow}>
                    <Box sx={style.creditsTdCreditType}>{item.taxName || item.name || "—"}</Box>
                    <Box sx={style.creditsTdMode}>{item.type || "-"}</Box>
                    <Box sx={style.creditsTdApplicableOn}>{item.paidBy || "—"}</Box>
                    <Box sx={style.creditsTdTotalCredits}>
                      {item.employeeValue !== undefined && item.employeeValue !== null ? item.employeeValue : 0}
                    </Box>
                    <Box sx={style.creditsTdTotalCredits}>
                      {item.employerValue !== undefined && item.employerValue !== null ? item.employerValue : 0}
                    </Box>
                    <Box sx={style.creditsTdActions}>
                      <Switch
                        checked={currentToggleValue}
                        onChange={async (e) => {
                          const desiredState = e.target.checked;
                          const previousState = persistedToggleValue !== undefined
                            ? persistedToggleValue
                            : item?.isAssigned || item?.isApplicable || false;
                          
                          setTaxRulesToggleState((prev) => ({
                            ...prev,
                            [toggleKey]: desiredState,
                          }));

                          const revertToggle = () =>
                            setTaxRulesToggleState((prev) => ({
                              ...prev,
                              [toggleKey]: previousState,
                            }));

                          try {
                            const taxRuleId = item?.taxRule || item?._id || item?.taxRuleId;
                            const employeeTaxRuleId = item?.employeeTaxRuleId || item?.id;

                            const payload = {
                              employeeId: userData?._id,
                              taxRuleId: taxRuleId,
                              isApplicable: desiredState,
                            };

                            if (employeeTaxRuleId) {
                              payload._id = employeeTaxRuleId;
                            }

                            await handleTaxRuleApply(payload);
                            if (refreshTaxRules) {
                              await refreshTaxRules();
                            }
                          } catch (error) {
                            revertToggle();
                            const msg = error?.response?.data?.message || error?.message || "Failed to update tax rule";
                            showAlert(msg, "error");
                          }
                        }}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={style.creditsTableRow}>
                <Box sx={style.creditsTdCreditType} colSpan={6} style={{ textAlign: 'center', width: '100%' }}>
                  No Tax Rules Available
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: "15px", mt: 3, mb: 3 }}>
        <Box sx={style.creditsHeader}>
          <Typography sx={style.creditsTitle}>Credits</Typography>
        </Box>
        <Box sx={style.creditsTableContainer}>
          <Box sx={style.creditsTable}>
            <Box sx={style.creditsTableHeader}>
              <Box sx={style.creditsThCreditType}>Credit Type</Box>
              <Box sx={style.creditsThMode}>Mode</Box>
              <Box sx={style.creditsThApplicableOn}>Applicable on</Box>
              <Box sx={style.creditsThTotalCredits}>Total Credits</Box>
              <Box sx={style.creditsThActions}>Actions</Box>
            </Box>
            {Array.isArray(creditsListData) && creditsListData.length > 0 ? (
              creditsListData.map((item, idx) => {
                // Simple toggle key like Leaves section
                const toggleKey = item?._id || idx;
                const persistedToggleValue = creditToggleState[toggleKey];
                const currentToggleValue =
                  persistedToggleValue !== undefined
                    ? persistedToggleValue
                    : item?.isApplicable || false;

                // Get employee credit ID from singleCreditTypes (similar to how Leaves uses item._id)
                const getEmployeeCreditId = () => {
                  if (!Array.isArray(singleCreditTypes)) return null;
                  const found = singleCreditTypes.find((s) => s.credit === item?._id || s.type === item?.type);
                  if (!found) return null;
                  return found.employeeCreditId || found.creditId || found._id || null;
                };

                return (
                  <Box key={idx} sx={style.creditsTableRow}>
                    <Box sx={style.creditsTdCreditType}>{item.type || "—"}</Box>
                    <Box sx={style.creditsTdMode}>{item?.rateType || "-"}</Box>
                    <Box sx={style.creditsTdApplicableOn}>{"Employee"}</Box>
                    <Box sx={style.creditsTdTotalCredits}>
                      {item.totalNoOfCreditForEmp !== undefined && item.totalNoOfCreditForEmp !== null && item.totalNoOfCreditForEmp > 0
                        ? item.totalNoOfCreditForEmp 
                        : (employeeBeneficiaryValues?.[item._id] !== undefined 
                            ? employeeBeneficiaryValues[item._id] 
                            : 0)}
                    </Box>
                    <Box sx={style.creditsTdActions}>
                      <Box sx={style.creditsActionIconBlue} onClick={async () => {
                        setCreditId(item._id);
                        await getAllBenificiaries(item._id);
                        setIsBeneficiaryModalOpen(true);
                        setSelectedItem(item);
                      }}>
                        <PeopleAltIcon sx={{ fontSize: "16px", color: "#ffffff" }} />
                      </Box>
                      <Switch
                        checked={currentToggleValue}
                        onChange={async (e) => {
                          const desiredState = e.target.checked;
                          const previousState = persistedToggleValue !== undefined
                            ? persistedToggleValue
                            : item?.isApplicable || false;
                          
                          setCreditToggleState((prev) => ({
                            ...prev,
                            [toggleKey]: desiredState,
                          }));

                          const revertToggle = () =>
                            setCreditToggleState((prev) => ({
                              ...prev,
                              [toggleKey]: previousState,
                            }));

                          try {
                            // Get employee credit mapping ID (like item._id in Leaves)
                            let employeeCreditId = getEmployeeCreditId();
                            
                            if (!employeeCreditId) {
                              // No existing mapping, use POST to create (like Leaves when item._id is null)
                              await handlePostCreditApply({
                                employee: userData?._id,
                                credit: item?._id,
                                isApplicable: desiredState
                              });
                            } else {
                              // Mapping exists, use PUT to update (like Leaves when item._id exists)
                              await handleCreditApply({
                                creditId: employeeCreditId,
                                isApplicable: desiredState
                              });
                            }
                            
                            // Refresh data after API call
                            await getAllCredits();
                            if (typeof refreshSingleCreditTypes === "function") {
                              await refreshSingleCreditTypes(true);
                            }
                            setCreditToggleState((prev) => ({
                              ...prev,
                              [toggleKey]: desiredState,
                            }));
                          } catch (error) {
                            revertToggle();
                            const msg = error?.response?.data?.message || error?.message || "Failed to update credit";
                            showAlert(msg, "error");
                          }
                        }}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Box>
                )
              }
              )
            ) : (
              <Box sx={style.creditsTableRow}>
                <Box sx={style.creditsTdCreditType} colSpan={5} style={{ textAlign: 'center', width: '100%' }}>
                  No Credits Available
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: "15px", mt: 3, mb: 3 }}>
        <Box sx={style.creditsHeader}>
          <Typography sx={style.creditsTitle}>Contributions</Typography>
          <Box sx={style.manageButton} onClick={() => {
            setSelectedItem(null);
            setIsEditMode(false);
            setActiveModal("contribution");
          }}
          >Manage</Box>
        </Box>
        <Box sx={style.creditsTableContainer}>
          <Box sx={style.creditsTable}>
            <Box sx={style.creditsTableHeader}>
              <Box sx={style.creditsThCreditType}>Contribution Type</Box>
              <Box sx={style.creditsThMode}>Mode</Box>
              <Box sx={style.creditsThApplicableOn}>Applicable on</Box>
              <Box sx={style.creditsThTotalCredits}>Total Contribution</Box>
              <Box sx={style.creditsThActions}>Actions</Box>
            </Box>
            {Array.isArray(getContributionsdata) && getContributionsdata.length > 0 ? (
              getContributionsdata.map((item, idx) => (
                <Box key={idx} sx={style.creditsTableRow}>
                  <Box sx={style.creditsTdCreditType}>{item.type || "—"}</Box>
                  <Box sx={style.creditsTdMode}>{item.employeeCont?.type || "-"}</Box>
                  <Box sx={style.creditsTdApplicableOn}>{item?.applicableOn || "—"}</Box>
                  <Box sx={style.creditsTdTotalCredits}>
                    {buildTotalDisplay({
                      applicableOn: item?.applicableOn,
                      employeeValues: [item?.employeeCont?.rateValue],
                      employerValues: [item?.employerCont?.rateValue],
                    })}
                  </Box>
                  <Box sx={style.creditsTdActions}>
                    <Box sx={style.creditsActionIconGreen} onClick={() => {
                      setSelectedItem(item);
                      setIsEditMode(true);
                      setActiveModal("contribution");
                    }}>
                      <VisibilityIcon sx={{ fontSize: "20px", color: "#878787" }} />
                    </Box>
                    <Box sx={style.creditsActionIconRed} onClick={() => handleContributionDelete(item._id)}>
                      <DeleteIcon sx={{ fontSize: "20px", color: "#ff0000" }} />
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={style.creditsTableRow}>
                <Box sx={style.creditsTdCreditType} colSpan={5} style={{ textAlign: 'center', width: '100%' }}>
                  No Contributions Available
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: "15px", mt: 3, mb: 3 }}>
        <Box sx={style.creditsHeader}>
          <Typography sx={style.creditsTitle}>Allowances</Typography>
          <Box sx={style.manageButton} onClick={() => {
            setSelectedItem(null);
            setIsEditMode(false);
            setActiveModal("allowance");
          }}
          >Manage</Box>
        </Box>
        <Box sx={style.creditsTableContainer}>
          <Box sx={style.creditsTable}>
            <Box sx={style.creditsTableHeader}>
              <Box sx={style.creditsThCreditType}>Allowances Type</Box>
              <Box sx={style.creditsThMode}>Mode</Box>
              <Box sx={style.creditsThApplicableOn}>Applicable on</Box>
              <Box sx={style.creditsThTotalCredits}>Total Allowances</Box>
              <Box sx={style.creditsThActions}>Actions</Box>
            </Box>
            {Array.isArray(getAllowance) && getAllowance.length > 0 ? (
              getAllowance.map((item, idx) => (
                <Box key={idx} sx={style.creditsTableRow}>
                  <Box sx={style.creditsTdCreditType}>{item.type || "—"}</Box>
                  <Box sx={style.creditsTdMode}>{item?.employeeCont?.type || item?.employerCont?.type || "-"}</Box>
                  <Box sx={style.creditsTdApplicableOn}>{item.applicableOn || "—"}</Box>
                  <Box sx={style.creditsTdTotalCredits}>
                    {buildTotalDisplay({
                      applicableOn: item?.applicableOn,
                      employeeValues: [
                        item?.employeeCont?.rateValue,
                        item?.employeeCont?.amount,
                      ],
                      employerValues: [
                        item?.employerCont?.rateValue,
                        item?.employerCont?.amount,
                      ],
                      fallbacks: [item?.rateValue],
                    })}
                  </Box>
                  <Box sx={style.creditsTdActions}>
                    <Box sx={style.creditsActionIconGreen} onClick={() => {
                      setSelectedItem(item);
                      setIsEditMode(true);
                      setActiveModal("allowance");
                    }}>
                      <VisibilityIcon sx={{ fontSize: "20px", color: "#878787" }} />
                    </Box>
                    <Box sx={style.creditsActionIconRed} onClick={() => handleAllowancesDelete(item._id)}>
                      <DeleteIcon sx={{ fontSize: "20px", color: "#ff0000" }} />
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={style.creditsTableRow}>
                <Box sx={style.creditsTdCreditType} colSpan={5} style={{ textAlign: 'center', width: '100%' }}>
                  No Allowances Available
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: "15px", mt: 3, mb: 3 }}>
        <Box sx={style.creditsHeader}>
          <Typography sx={style.creditsTitle}>Fringe Benefits</Typography>
          <Box sx={style.manageButton} onClick={() => {
            setSelectedItem(null);
            setIsEditMode(false);
            setActiveModal("fringe");
          }}
          >Manage</Box>
        </Box>
        <Box sx={style.creditsTableContainer}>
          <Box sx={style.creditsTable}>
            <Box sx={style.creditsTableHeader}>
              <Box sx={style.creditsThCreditType}>Fringe Benefits Type</Box>
              <Box sx={style.creditsThMode}>Mode</Box>
              <Box sx={style.creditsThApplicableOn}>Applicable on</Box>
              <Box sx={style.creditsThTotalCredits}>Total Fringe Benefits</Box>
              <Box sx={style.creditsThActions}>Actions</Box>
            </Box>
            {Array.isArray(getFringeBenefit) && getFringeBenefit.length > 0 ? (
              getFringeBenefit.map((item, idx) => (
                <Box key={idx} sx={style.creditsTableRow}>
                  <Box sx={style.creditsTdCreditType}>{item.type || "—"}</Box>
                  <Box sx={style.creditsTdMode}>{item?.employeeCont?.type || item?.employerCont?.type || "-"}</Box>
                  <Box sx={style.creditsTdApplicableOn}>{item.applicableOn || "—"}</Box>
                  <Box sx={style.creditsTdTotalCredits}>
                    {buildTotalDisplay({
                      applicableOn: item?.applicableOn,
                      employeeValues: [
                        item?.employeeCont?.rateValue,
                        item?.employeeCont?.amount,
                      ],
                      employerValues: [
                        item?.employerCont?.rateValue,
                        item?.employerCont?.amount,
                      ],
                      fallbacks: [item?.amount, item?.rateValue],
                    })}
                  </Box>
                  <Box sx={style.creditsTdActions}>
                    <Box sx={style.creditsActionIconGreen} onClick={() => {
                      setSelectedItem(item);
                      setIsEditMode(true);
                      setActiveModal("fringe");
                    }}>
                      <VisibilityIcon sx={{ fontSize: "20px", color: "#878787" }} />
                    </Box>
                    <Box sx={style.creditsActionIconRed} onClick={() => handleFringeBenefitsDelete(item._id)}>
                      <DeleteIcon sx={{ fontSize: "20px", color: "#ff0000" }} />
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={style.creditsTableRow}>
                <Box sx={style.creditsTdCreditType} colSpan={5} style={{ textAlign: 'center', width: '100%' }}>
                  No Fringe Benefits Available
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: "15px", mt: 3, mb: 5 }}>
        <Box sx={style.creditsHeader}>
          <Typography sx={style.creditsTitle}>Levies</Typography>
          <Box sx={style.manageButton} onClick={() => {
            setSelectedItem(null);
            setIsEditMode(false);
            setActiveModal("levy");
          }}
          >Manage</Box>
        </Box>
        <Box sx={style.creditsTableContainer}>
          <Box sx={style.creditsTable}>
            <Box sx={style.creditsTableHeader}>
              <Box sx={style.creditsThCreditType}>Levies Type</Box>
              <Box sx={style.creditsThMode}>Mode</Box>
              <Box sx={style.creditsThApplicableOn}>Applicable on</Box>
              <Box sx={style.creditsThTotalCredits}>Total Levies</Box>
              <Box sx={style.creditsThActions}>Actions</Box>
            </Box>
            {Array.isArray(getLevies) && getLevies.length > 0 ? (
              getLevies.map((item, idx) => (
                <Box key={idx} sx={style.creditsTableRow}>
                  <Box sx={style.creditsTdCreditType}>{item.type || "—"}</Box>
                  <Box sx={style.creditsTdMode}>{item.employeeCont?.type || item.mode || ""}</Box>
                  <Box sx={style.creditsTdApplicableOn}>{item.applicableOn || "—"}</Box>
                  <Box sx={style.creditsTdTotalCredits}>
                    {buildTotalDisplay({
                      applicableOn: item?.applicableOn,
                      employeeValues: [item?.employeeCont?.rateValue],
                      employerValues: [item?.employerCont?.rateValue],
                    })}
                  </Box>
                  <Box sx={style.creditsTdActions}>
                    <Box sx={style.creditsActionIconGreen} onClick={() => {
                      setSelectedItem(item);
                      setIsEditMode(true);
                      setActiveModal("levy");
                    }}>
                      <VisibilityIcon sx={{ fontSize: "20px", color: "#878787" }} />
                    </Box>
                    <Box sx={style.creditsActionIconRed} onClick={() => handleLeviesDelete(item._id)}>
                      <DeleteIcon sx={{ fontSize: "20px", color: "#ff0000" }} />
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={style.creditsTableRow}>
                <Box sx={style.creditsTdCreditType} colSpan={5} style={{ textAlign: 'center', width: '100%' }}>
                  No Levies Available
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: "15px", mt: 3, mb: 5 }}>
        <Box sx={style.creditsHeader}>
          <Typography sx={style.creditsTitle}>Leaves</Typography>
        </Box>
        <Box sx={style.creditsTableContainer}>
          <Box sx={style.creditsTable}>
            <Box sx={style.creditsTableHeader}>
              <Box sx={style.creditsThCreditType}>Type</Box>
              <Box sx={style.creditsThTotalCredits}>Yearly Cap</Box>
              <Box sx={style.creditsThActions}>Action</Box>
            </Box>
            {Array.isArray(getLeaves) && getLeaves.length > 0 ? (
              getLeaves.map((item, idx) => {
                const toggleKey = item?._id || idx;
                const persistedToggleValue = leavesToggleState[toggleKey];
                const currentToggleValue =
                  persistedToggleValue !== undefined
                    ? persistedToggleValue
                    : item?.isApplicable || false;

                return (
                  <Box key={idx} sx={style.creditsTableRow}>
                    <Box sx={style.creditsTdCreditType}>
                      {item?.leave_type?.typeName || "—"}
                    </Box>
                    <Box sx={style.creditsTdTotalCredits}>
                      {item?.yearlyCap !== undefined && item?.yearlyCap !== null ? item.yearlyCap : "—"}
                    </Box>
                    <Box sx={style.creditsTdActions}>
                      <Switch
                        checked={currentToggleValue}
                        onChange={async (e) => {
                          const desiredState = e.target.checked;
                          const previousState = persistedToggleValue !== undefined
                            ? persistedToggleValue
                            : item?.isApplicable || false;
                          
                          setLeavesToggleState((prev) => ({
                            ...prev,
                            [toggleKey]: desiredState,
                          }));

                          const revertToggle = () =>
                            setLeavesToggleState((prev) => ({
                              ...prev,
                              [toggleKey]: previousState,
                            }));

                          try {
                            // Extract start and end from leave_type
                            const startDate = item?.leave_type?.start || item?.start || null;
                            const endDate = item?.leave_type?.end || item?.end || null;
                            
                            if (!item?._id || item._id === null) {
                              const createPayload = {
                                employee: userData?._id,
                                start: startDate,
                                end: endDate,
                                method: item?.leave_type?.method || null,
                                isApplicable: desiredState,
                                leave_type: item?.leave_type?.type || null,
                                yearlyCap: item?.yearlyCap !== undefined ? item.yearlyCap : null,
                              };
                              await handleLeaveBalanceCreate(createPayload);
                            } else {
                              const updatePayload = {
                                isApplicable: desiredState,
                                alreadyUsedBalance: item?.alreadyUsedBalance !== undefined ? item.alreadyUsedBalance : null,
                                balance: item?.balance !== undefined ? item.balance : null,
                                isBalanceUsed: item?.isBalanceUsed !== undefined ? item.isBalanceUsed : null,
                                leave_type: item?.leave_type?.type || null,
                                yearlyCap: item?.yearlyCap !== undefined ? item.yearlyCap : null,
                                start: startDate,
                                end: endDate,
                              };
                              await handleLeaveBalanceUpdate(item._id, updatePayload);
                            }
                            if (refreshLeaves) {
                              await refreshLeaves();
                              setLeavesToggleState((prev) => ({
                                ...prev,
                                [toggleKey]: desiredState,
                              }));
                            }
                          } catch (error) {
                            revertToggle();
                            const msg = error?.response?.data?.message || error?.message || "Failed to update leave balance";
                            showAlert(msg, "error");
                          }
                        }}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={style.creditsTableRow}>
                <Box sx={style.creditsTdCreditType} colSpan={4} style={{ textAlign: 'center', width: '100%' }}>
                  No Leaves Available
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <ManageConfigurationModal
        open={activeModal !== null}
        onClose={closeModal}
        name={activeModal === 'contribution' ? 'Contribution' : activeModal === 'allowance' ? "Allowances" : activeModal === 'fringe' ? "Fringe Benefits" : activeModal === "levy" ? "Levies" : "Configuration"}
        contributionType={contributionType}
        allowanceType={allowanceType}
        fringeType={fringeType}
        leviesType={leviesType}
        handleAllContribution={handleAllContribution}
        selectedItem={selectedItem}
        isEditMode={isEditMode}
        existingData={{
          contributions: getContributionsdata,
          allowances: getAllowance,
          fringeBenefits: getFringeBenefit,
          levies: getLevies
        }}
      />

      <BeneficiaryModal employeeId={""} open={isBeneficiaryModalOpen} onClose={() => setIsBeneficiaryModalOpen(false)} userData={userData} handleBeniSaved={handleBeniSaved} creditId={creditId} handleUpdateBeneficiary={handleUpdateBeneficiary} beneficiariesList={beneficiaries} handleDeleteBeneficiary={handleDeleteBeneficiary} getAllBenificiaries={getAllBenificiaries} getAllCredits={getAllCredits} selectedItem={selectedItem} />
    </>
  );
};
export default additionalInformation;